import React, { useRef } from 'react';
import { gsap } from 'gsap';
import { prefersReducedMotion, useIsoLayoutEffect } from './motion';

const srOnly = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

/* Animated number. Counts from 0 on mount and from the previous value on
   change. The text is written straight to the DOM node, so React does not
   re-render per frame. While it counts, the box keeps the width of the final
   value, so nothing next to it shifts. Screen readers get the final value
   only. */
export default function CountUp({
  value,
  suffix = '',
  prefix = '',
  duration = 1.2,
  delay = 0,
  decimals = 0,
  locale = 'ru-RU',
  style,
  className,
}) {
  const numRef = useRef(null);
  const fromRef = useRef(0);
  const target = Number(value) || 0;
  const format = (v) =>
    `${prefix}${v.toLocaleString(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })}${suffix}`;
  const finalText = format(target);

  useIsoLayoutEffect(() => {
    const el = numRef.current;
    if (!el) return undefined;
    const from = fromRef.current;
    el.style.minWidth = '';
    el.textContent = finalText;
    if (prefersReducedMotion() || from === target) {
      fromRef.current = target;
      return undefined;
    }
    // Reserve the final width so the line never reflows mid-count
    el.style.minWidth = `${el.getBoundingClientRect().width}px`;
    const counter = { v: from };
    el.textContent = format(from);
    const tween = gsap.to(counter, {
      v: target,
      duration,
      delay,
      ease: 'power3.out',
      onUpdate: () => {
        el.textContent = format(decimals ? counter.v : Math.round(counter.v));
      },
      onComplete: () => {
        el.textContent = finalText;
        el.style.minWidth = '';
      },
    });
    return () => {
      // Resume from what is on screen if the value changes mid-count
      fromRef.current = counter.v;
      tween.kill();
      el.textContent = finalText;
      el.style.minWidth = '';
    };
  }, [target, suffix, prefix, decimals]);

  return (
    <span
      className={className}
      style={{ position: 'relative', display: 'inline-block', ...style }}
    >
      <span
        ref={numRef}
        aria-hidden="true"
        style={{
          display: 'inline-block',
          fontVariantNumeric: 'tabular-nums',
          textAlign: 'left',
        }}
      >
        {finalText}
      </span>
      <span style={srOnly}>{finalText}</span>
    </span>
  );
}
